import { beginNavigation, endNavigation } from '../../shared/navigation';
import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../app/store';
import { switchOrganization } from '../../app/sessionSlice';
import { authApi } from '../../core/api/client';
import type { DestinationOrganization } from '../../core/api/types';
import { DestinationPicker, destinationRoute, type DestinationChoice } from '../auth/DestinationPicker';

export function SwitchAppPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<DestinationOrganization[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setError(null);
    setOrganizations(null);
    void authApi.destinations().then((data) => {
      if (active) setOrganizations(data.organizations);
    }).catch((caught) => {
      if (active) setError(caught instanceof Error ? caught.message : t('switchApp.loadError', 'Applications could not be loaded.'));
    });
    return () => { active = false; };
  }, [attempt, t]);

  async function open({ organizationId, destination }: DestinationChoice) {
    if (busy) return;
    beginNavigation();
    setBusy(true);
    setError(null);
    try {
      await dispatch(switchOrganization({ organizationId, productCode: 'PLATFORM' })).unwrap();
      navigate(destinationRoute(destination));
    } catch (caught) {
      endNavigation();
      setError(caught instanceof Error ? caught.message : t('switchApp.openError', 'The application could not be opened.'));
    } finally {
      setBusy(false);
    }
  }

  // Same cards as the sign-in destination popup (Hub 02 §2.1), loaded from GET auth/destinations.
  return <Box sx={{ p: { xs: 1, md: 3 }, display: 'grid', justifyItems: 'center' }}>
    {!organizations && error && <Alert severity="error" sx={{ width: '100%', maxWidth: 880 }} action={<Button color="inherit" onClick={() => setAttempt(value => value + 1)}>
      {t('switchApp.retry', 'Retry')}
    </Button>}>{error}</Alert>}
    {!organizations && !error && <CircularProgress aria-label={t('switchApp.loading', 'Loading applications')} />}
    {organizations?.length === 0 && <Alert severity="info">{t('switchApp.empty', 'No applications are available for your active memberships.')}</Alert>}
    {organizations && organizations.length > 0 && <DestinationPicker organizations={organizations} busy={busy} error={error}
      onOpen={(choice) => void open(choice)} />}
  </Box>;
}
